import { Contact } from '../models/contact.model';
import { normalizePhoneNumber } from './normalize-phone-number.function';
import { CustomerAddress } from '../models/customer-address.model';
import { ShipmentAddress } from '../models/plastics-shipment.model';
import { UpsShipmentParty } from '../models/ups-shipment.model';
import { emptyInternationalAddress } from '../models/international-address.model';

/**
 * Rebuild a shipment's destination/origin from the DOCUMENTS it references, instead of from a
 * copied snapshot stored on the shipment itself.
 *
 * Shipments (UPS and plastics alike) hold `addressId`/`contactId` references. These helpers take
 * the already-loaded `CustomerAddress`/`Contact` docs — callers batch-load them once per page via
 * `loadAddressesByIds`/`loadContactsByIds` — and assemble the shape each surface already expects,
 * so display code stays synchronous.
 *
 * IDS ONLY: the resolved doc is the sole source — there is no snapshot to fall back to, because
 * shipments no longer store one. Legacy records get real docs via the snapshot→ids migration
 * before this ships.
 *
 * NO NAME FALLBACK: `receiverName` is entered manually on the address and blank means blank —
 * a party's company line is exactly what the address doc says, never the customer name.
 */

/** Resolve a UPS ship-to / ship-from party. `residential` is not an address property (a delivery
 *  is residential, a reusable address isn't), so it comes from the shipment's own flat column. */
export function resolveUpsParty(
    address: CustomerAddress | undefined,
    contact: Contact | undefined,
    residential: boolean,
): UpsShipmentParty {
    if (!address) {
        return { company: '', address: emptyInternationalAddress(), contactName: '', phone: '', email: '', residential };
    }
    return {
        // UPS labels the destination line "company"; the address book calls the party at the
        // address the receiver — same field, verbatim from the doc.
        company: address.receiverName ?? '',
        address: address.address ?? emptyInternationalAddress(),
        contactName: contact?.name ?? '',
        // Digits-only house rule; legacy contacts may still hold a formatted string.
        phone: normalizePhoneNumber(contact?.primaryPhoneNumber?.number),
        email: contact?.primaryEmail ?? '',
        residential,
    };
}

/** Resolve a plastics shipment's billing / shipping address block (packing slip, BOL, view page). */
export function resolveShipmentAddress(
    address: CustomerAddress | undefined,
    contact: Contact | undefined,
): ShipmentAddress {
    if (!address) {
        return {
            address: emptyInternationalAddress(),
            receiverName: '',
            attentionName: '',
            phone: '',
        };
    }
    return {
        address: address.address ?? emptyInternationalAddress(),
        receiverName: address.receiverName ?? '',
        attentionName: contact?.name ?? '',
        phone: normalizePhoneNumber(contact?.primaryPhoneNumber?.number),
    };
}

/**
 * Every address/contact id a UPS shipment references, for a batched pre-load. Callers flatten
 * these across a list and issue ONE `listByIds` per collection.
 */
export function upsShipmentDocIds(shipment: { addressId?: string; contactId?: string; shipFromAddressId?: string; shipFromContactId?: string }): { addressIds: string[]; contactIds: string[] } {
    const keep = (...ids: (string | undefined)[]) => ids.map(id => (id ?? '').trim()).filter(id => !!id);
    return {
        addressIds: keep(shipment.addressId, shipment.shipFromAddressId),
        contactIds: keep(shipment.contactId, shipment.shipFromContactId),
    };
}

/** The plastics counterpart of `upsShipmentDocIds`. */
export function plasticsShipmentDocIds(shipment: { billingAddressId?: string; billingContactId?: string; shippingAddressId?: string; shippingContactId?: string }): { addressIds: string[]; contactIds: string[] } {
    const keep = (...ids: (string | undefined)[]) => ids.map(id => (id ?? '').trim()).filter(id => !!id);
    return {
        addressIds: keep(shipment.billingAddressId, shipment.shippingAddressId),
        contactIds: keep(shipment.billingContactId, shipment.shippingContactId),
    };
}

/** Index loaded docs by id — the lookup shape every resolving surface holds. */
export function byId<T extends { id: string }>(docs: T[]): Map<string, T> {
    return new Map((docs ?? []).map(doc => [doc.id, doc]));
}
